/**
 * First-party marketing attribution (gclid, UTMs). Stored locally until signup.
 * Not gated on analytics cookies — disclosed in Privacy Policy.
 */

const STORAGE_KEY = 'clarcv_attribution';
const TRACKED_KEYS = [
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

const isBrowser = () => typeof window !== 'undefined';

const readStore = () => {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (data) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private mode */
  }
};

/**
 * Parse gclid / UTMs from a URL search string.
 *
 * @param {string} [search]
 * @returns {Record<string, string>}
 */
export const parseAttributionSearch = (search = '') => {
  const params = new URLSearchParams((search || '').replace(/^\?/, ''));
  const out = {};
  TRACKED_KEYS.forEach((key) => {
    const value = (params.get(key) || '').trim();
    if (value) out[key] = value.slice(0, 255);
  });
  return out;
};

/**
 * Merge new query params into stored attribution (first-touch wins per field).
 *
 * @param {string} [search]
 * @param {string} [pathname]
 */
export const captureAttributionFromLocation = (search = '', pathname = '') => {
  if (!isBrowser()) return;
  const incoming = parseAttributionSearch(search);
  if (Object.keys(incoming).length === 0 && !pathname) return;

  const current = readStore();
  const next = { ...current };
  TRACKED_KEYS.forEach((key) => {
    if (!next[key] && incoming[key]) {
      next[key] = incoming[key];
    }
  });
  if (pathname && !next.landing_path) {
    next.landing_path = pathname.slice(0, 500);
  }
  if (!next.referrer && document.referrer) {
    next.referrer = document.referrer.slice(0, 500);
  }
  next.captured_at = next.captured_at || new Date().toISOString();
  writeStore(next);
};

/**
 * Read stored attribution snapshot.
 *
 * @returns {Record<string, string>}
 */
export const getStoredAttribution = () => readStore();

/**
 * Parse GA4 client id from the ``_ga`` cookie when present.
 *
 * @returns {string}
 */
export const getGaClientId = () => {
  if (!isBrowser()) return '';
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return '';
  const raw = decodeURIComponent(match[1]);
  const parts = raw.split('.');
  if (parts.length >= 4) {
    return `${parts[2]}.${parts[3]}`;
  }
  return raw.slice(0, 64);
};

/**
 * Build the JSON payload sent on email / Google signup.
 *
 * @param {{ analyticsConsent?: boolean, adConsent?: boolean }} [opts]
 * @returns {Record<string, unknown>}
 */
export const buildSignupAttributionPayload = ({
  analyticsConsent = false,
  adConsent = false,
} = {}) => {
  const stored = readStore();
  return {
    gclid: stored.gclid || '',
    utm_source: stored.utm_source || '',
    utm_medium: stored.utm_medium || '',
    utm_campaign: stored.utm_campaign || '',
    utm_term: stored.utm_term || '',
    utm_content: stored.utm_content || '',
    landing_path: stored.landing_path || '',
    referrer: stored.referrer || '',
    ga_client_id: getGaClientId(),
    analytics_consent: Boolean(analyticsConsent),
    ad_consent: Boolean(adConsent),
  };
};
